import {
  type AdvisorIdentity,
  buildFieldSections,
  buildSignerRoster,
  resolveSignerContact,
} from '@bmo/form-core';

import type { WorkflowTemplate } from '@/api/workflows';

import type { FieldValue } from './FieldRenderer';

/** The advisor side of `resolveSignerContact`, unused for a client role. */
const NO_ADVISOR = {} as AdvisorIdentity;

/**
 * The primary client's name for the "CLIENT:" badge beside the form title (RDSP GAMXDPT-275,
 * QA D-02), or null while there is none to show.
 *
 * No new configuration: the workflow already says who its client is. The signing roles map each
 * party's name fields, and the first client role in the roster — gating conditions applied, first
 * instance — is the primary account holder, however the form was answered (an individual, a
 * public body, or the beneficiary who is also the holder). A second account holder is a later
 * roster entry, so it never reaches the badge (AC4).
 *
 * Shown only once the step holding that name has been passed with Next (AC1, AC6). The name is
 * read live after that, so a correction made on that step shows as soon as the advisor moves on
 * (AC5).
 */
export function primaryClientName(
  template: WorkflowTemplate,
  values: Record<string, FieldValue>,
  passedStepIds: ReadonlySet<string>,
): string | null {
  const roles = template.steps.find((s) => s.type === 'OneSpanESign' && s.oneSpanConfig)
    ?.oneSpanConfig?.roles;
  if (!roles?.length) return null;
  const sections = buildFieldSections(template);
  const primary = buildSignerRoster(roles, sections, values).find(
    ({ role }) => role.type !== 'advisor',
  );
  const nameFieldId = primary?.role.fieldMappings?.nameFieldId;
  if (!primary || !nameFieldId) return null;

  const home = template.steps.find(
    (s) =>
      s.type === 'DynamicForm' &&
      s.sections.some((sec) =>
        sec.rows.some((r) => r.columns.some((c) => c.field?.id === nameFieldId)),
      ),
  );
  if (!home || !passedStepIds.has(home.id)) return null;

  const name = resolveSignerContact(
    primary.role,
    values,
    sections,
    primary.instanceIdx,
    NO_ADVISOR,
  ).fullName.trim();
  return name || null;
}
